import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { OWNER_PROFILE, PORTAL_CONTACT } from "@/data/portalConfig";
import { api } from "@/lib/api";

const SITE_CONTEXT_CACHE_PREFIX = "site-context-cache:";

const defaultSiteContext = {
  mode: "global",
  owner_id: null,
  requested_slug: null,
  source_disclaimer: null,
  invite_only: false,
  invite_only_message: null,
  dashboard_path: null,
  district_intelligence: [],
  contact: {
    name: OWNER_PROFILE.name,
    email: PORTAL_CONTACT.email,
    whatsapp_display: PORTAL_CONTACT.whatsappDisplay,
    whatsapp_digits: PORTAL_CONTACT.whatsappDigits,
    cea_registration_number: null,
    profile_photo_url: null,
    subdomain_slug: null,
    team_name: null,
    team_logo_url: null,
    verified_badge_label: null,
    advisor_tagline: null,
    advisor_bio: null,
    specialties: [],
    service_regions: [],
    preferred_categories: [],
    response_sla: null,
  },
};

const SiteContext = createContext({
  siteContext: defaultSiteContext,
  isAgentPath: false,
  isSiteContextLoading: false,
  branding: defaultSiteContext.contact,
  requestParams: {},
  buildBrandedPath: (path = "") => path,
  getWhatsAppHref: () => `https://wa.me/${PORTAL_CONTACT.whatsappDigits}`,
  leadFormPath: PORTAL_CONTACT.leadFormPath,
  refreshSiteContext: async () => {},
});

const readCachedSiteContext = (slug) => {
  if (typeof window === "undefined" || !slug) return null;
  try {
    const rawValue = window.sessionStorage.getItem(`${SITE_CONTEXT_CACHE_PREFIX}${slug}`);
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedSiteContext = (slug, context) => {
  if (typeof window === "undefined" || !slug || !context) return;
  try {
    window.sessionStorage.setItem(`${SITE_CONTEXT_CACHE_PREFIX}${slug}`, JSON.stringify(context));
  } catch {
    // caching should never block route rendering
  }
};

export const SiteContextProvider = ({ children }) => {
  const location = useLocation();
  const siteContextRequestRef = useRef({ key: null, promise: null });
  const deriveAgentSlugFromPath = useCallback((pathname) => {
    const match = String(pathname || "").match(/^\/agent\/([^/]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]).toLowerCase() : null;
  }, []);
  const initialRequestedSlug = typeof window !== "undefined" ? deriveAgentSlugFromPath(window.location.pathname) : null;
  const initialCachedContext = readCachedSiteContext(initialRequestedSlug);
  const [siteContext, setSiteContext] = useState(() => {
    if (initialCachedContext) return initialCachedContext;
    if (typeof window === "undefined") return defaultSiteContext;
    const requestedSlug = deriveAgentSlugFromPath(window.location.pathname);
    return { ...defaultSiteContext, requested_slug: requestedSlug };
  });
  const [isSiteContextLoading, setIsSiteContextLoading] = useState(Boolean(initialRequestedSlug && !initialCachedContext));

  const refreshSiteContext = useCallback(async () => {
    const requestedSlug = typeof window !== "undefined" ? deriveAgentSlugFromPath(window.location.pathname) || undefined : undefined;
    const inviteToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("invite") || undefined : undefined;
    const previewToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("preview_token") || undefined : undefined;
    const cachedContext = readCachedSiteContext(requestedSlug);
    const requestKey = JSON.stringify({ requestedSlug: requestedSlug || null, inviteToken: inviteToken || null, previewToken: previewToken || null });
    setSiteContext((current) => ({ ...current, requested_slug: requestedSlug || null }));
    if (cachedContext) {
      setSiteContext(cachedContext);
    }
    setIsSiteContextLoading(Boolean(requestedSlug && !cachedContext));
    try {
      const activeRequest =
        siteContextRequestRef.current.key === requestKey && siteContextRequestRef.current.promise
          ? siteContextRequestRef.current.promise
          : api.get("/site-context", { params: { agent_slug: requestedSlug, invite_token: inviteToken, preview_token: previewToken } }).then((response) => response.data || defaultSiteContext);
      siteContextRequestRef.current = { key: requestKey, promise: activeRequest };
      const nextContext = await activeRequest;
      if (typeof window !== "undefined" && requestedSlug && nextContext?.requested_slug && nextContext.requested_slug !== requestedSlug) {
        const normalizedPath = window.location.pathname.replace(/^\/agent\/[^/]+/i, `/agent/${nextContext.requested_slug}`);
        const nextUrl = `${normalizedPath}${window.location.search}${window.location.hash}`;
        window.history.replaceState({}, "", nextUrl);
      }
      if (requestedSlug || nextContext?.requested_slug) {
        writeCachedSiteContext(nextContext?.requested_slug || requestedSlug, nextContext);
      }
      setSiteContext(nextContext);
    } catch {
      if (!cachedContext) {
        setSiteContext({ ...defaultSiteContext, requested_slug: requestedSlug || null });
      }
    } finally {
      if (siteContextRequestRef.current.key === requestKey) {
        siteContextRequestRef.current = { key: null, promise: null };
      }
      setIsSiteContextLoading(false);
    }
  }, [deriveAgentSlugFromPath]);

  useEffect(() => {
    refreshSiteContext();
  }, [location.pathname, location.search, refreshSiteContext]);

  const value = useMemo(() => {
    const branding = siteContext.contact || defaultSiteContext.contact;
    const activePreviewToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("preview_token") || "" : "";
    const activeInviteToken = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("invite") || "" : "";
    const buildBrandedPath = (path = "") => {
      if (!siteContext.requested_slug) return path;
      const [pathWithoutHash, hash = ""] = String(path).split("#");
      const [pathname, queryString = ""] = pathWithoutHash.split("?");
      const params = new URLSearchParams(queryString);
      if (activePreviewToken && !params.has("preview_token")) {
        params.set("preview_token", activePreviewToken);
      }
      if (activeInviteToken && !params.has("invite")) {
        params.set("invite", activeInviteToken);
      }
      const nextQueryString = params.toString();
      const normalizedPathname = pathname === "/" ? "" : pathname;
      if (normalizedPathname.startsWith(`/agent/${siteContext.requested_slug}`)) {
        return `${pathname}${nextQueryString ? `?${nextQueryString}` : ""}${hash ? `#${hash}` : ""}`;
      }
      return `/agent/${siteContext.requested_slug}${normalizedPathname}${nextQueryString ? `?${nextQueryString}` : ""}${hash ? `#${hash}` : ""}`;
    };
    return {
      siteContext,
      isAgentPath: Boolean(siteContext.requested_slug),
      isSiteContextLoading,
      branding,
      requestParams: siteContext.requested_slug
        ? {
            agent_slug: siteContext.requested_slug,
            ...(typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview_token")
              ? { preview_token: new URLSearchParams(window.location.search).get("preview_token") }
              : {}),
          }
        : {},
      buildBrandedPath,
      leadFormPath: buildBrandedPath(PORTAL_CONTACT.leadFormPath),
      getWhatsAppHref: (message = "Hello, I’d like to enquire about a property.") => {
        const digits = siteContext.mode === "global" ? PORTAL_CONTACT.whatsappDigits : String(branding.whatsapp_digits || "").trim();
        return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : "#";
      },
      refreshSiteContext,
    };
  }, [isSiteContextLoading, refreshSiteContext, siteContext]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSiteBranding = () => useContext(SiteContext);